import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getProject } from '../../services/projectService';
import { getSignedUrl } from '../../services/storageService';
import Generate from './Generate';
import { Helmet } from 'react-helmet';
import Lines from '../common/Lines';
import ProgressScroll from '../common/ProgressScroll';
import Cursor from '../common/cusor';
import LoadingScreen from '../common/loader';
import Navbar from '../common/Navbar';
import { auth } from '../../config/firebase';

function BrandShow() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const brandId = searchParams.get('brand_id');
  const isPreview = searchParams.get('preview') === 'true';
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to get a fresh signed URL for a brand image
  const getFreshSignedUrl = async (imageUrl) => {
    try {
      if (!imageUrl) {
        return null;
      }
      
      // Extract the path from the URL
      let path = '';
      if (imageUrl.includes('storage.googleapis.com/') || imageUrl.includes('storage.cloud.google.com/')) {
        // Remove query parameters
        const urlWithoutQuery = imageUrl.split('?')[0];
        // Get the path after the domain
        const pathMatch = urlWithoutQuery.match(/\/(caspian-\d+\/images\/[^/]+)/);
        if (pathMatch) {
          path = pathMatch[1];
        }
      }
      
      if (!path) {
        return null;
      }
      
      // Build the GCS URL
      const gsUrl = `gs://${path}`;
      
      // Get the signed URL
      const signedUrl = await getSignedUrl(gsUrl);
      
      return signedUrl;
    } catch (error) {
      console.error('Error getting signed URL:', error);
      return null;
    }
  };

  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 3;
    const retryDelay = 1000; // 1 second

    const fetchProject = async () => {
      try {
        // Get the project directly
        const foundProject = await getProject(projectId);
        
        if (foundProject) {
          // If brand_id is provided, filter to only include that brand
          if (brandId) {
            const brand = foundProject.brands.find(b => b.id === brandId);
            if (!brand) {
              setError('Brand not found');
              setLoading(false);
              return;
            }

            // Check if brand is public when in preview mode
            if (isPreview && brand.access !== 'public') {
              setError('This brand is not publicly accessible');
              setLoading(false);
              return;
            }

            // Replace brands array with only the specified brand
            foundProject.brands = [brand];
            foundProject.selectedBrandIndex = 0;
          }

          // Get fresh signed URL for the brand image
          if (foundProject.brands?.[0]?.imageUrl) {
            const signedUrl = await getFreshSignedUrl(foundProject.brands[0].imageUrl);
            if (signedUrl) {
              foundProject.brands[0].imageUrl = signedUrl;
            }
          }
          
          setProject(foundProject);
          setLoading(false);
        } else {
          setError('Project not found');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching project:', err);
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(fetchProject, retryDelay);
        } else {
          if (err.message === 'Unauthorized access') {
            navigate('/sign-in');
            return;
          } else if (err.message === 'Project not found') {
            navigate('/page-404');
            return;
          } else {
            navigate('/sign-in');
            return;
          }
        }
      }
    };

    fetchProject();
  }, [projectId, brandId, isPreview, navigate]);

  if (loading) {
    return (
      <div style={{
        width: '100%',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        padding: '20px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #fff',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <span style={{ color: '#fff', fontSize: '16px' }}>Loading project...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        width: '100%',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 68, 68, 0.05)',
        padding: '20px'
      }}>
        <div style={{ color: '#ff4444', fontSize: '16px', textAlign: 'center' }}>
          {error}
          {error === 'Please sign in to view this project' && (
            <button 
              onClick={() => navigate('/login')}
              style={{
                marginTop: '20px',
                padding: '10px 20px',
                backgroundColor: '#ff4444',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Sign In
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Brand Details - NarrativeOS</title>
        <link rel="icon" href="/assets/imgs/favicon.ico" />
        <link rel="shortcut icon" href="/assets/imgs/favicon.ico" />
        <link rel="stylesheet" type="text/css" href="/assets/css/plugins.css" />
        <link rel="stylesheet" type="text/css" href="/assets/css/style.css" />
        <link
          rel="stylesheet"
          type="text/css"
          href="https://fonts.googleapis.com/css?family=Poppins:100,200,300,400,500,600,700,800,900&display=swap"
        />
        <link
          rel="stylesheet"
          type="text/css"
          href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@200;300;400;500;600;700&display=swap"
        />
      </Helmet>
      <LoadingScreen />
      <Cursor />
      <ProgressScroll />
      <Lines />
      <Navbar />
      <div id="smooth-wrapper">
        <div id="smooth-content">
          <main className="main-bg o-hidden">
            {project && (
              <Generate 
                initialProject={project}
                startOnLastSlide={true}
              />
            )}
          </main>
        </div>
      </div>
    </>
  );
}

export default BrandShow; 